import { useEffect } from "react";

import useQueryClient from "../../api/query";

const POLL_INTERVAL_MS = 30_000;

type OnUpdate = (org: OrganizationMessage) => void;

export default function useMaintenancePolling(
  enabled: boolean,
  onUpdate: OnUpdate,
) {
  const queryClient = useQueryClient();

  // biome-ignore lint/correctness/useExhaustiveDependencies: adding queryClient.getOrganization would change when this runs
  useEffect(() => {
    if (!enabled) return undefined;

    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | null = null;

    const check = async () => {
      try {
        const org = await queryClient.getOrganization();
        if (cancelled) return;
        onUpdate(org);
      } catch {
        // try again next tick
      }
    };

    const start = () => {
      if (intervalId !== null) return;
      intervalId = setInterval(check, POLL_INTERVAL_MS);
    };

    const stop = () => {
      if (intervalId === null) return;
      clearInterval(intervalId);
      intervalId = null;
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        check();
        start();
      } else {
        stop();
      }
    };

    if (document.visibilityState === "visible") {
      start();
    }
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [enabled, onUpdate]);
}
